import { Link } from "wouter";
import { ArrowRight, MessageSquare, Wrench, Lock, Users, BarChart3, UsersRound } from "lucide-react";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { FaqList, FinalBand } from "@/components/marketing/sections";
import { PricingCards } from "@/components/marketing/pricing-cards";
import { COMPARISON_ROWS, HOME_FAQ, OUTCOME_CARDS } from "@/data/core-pages";
import {
  HomeV2Founder,
  HomeV2Hero,
  HomeV2ProductEvidence,
  HomeV2Proof,
  HomeV2Setup,
} from "@/components/marketing/home-v2/story-sections";

const OUTCOME_ICONS = [MessageSquare, Wrench, Lock, Users, BarChart3, UsersRound];


const PAINS = [
  {
    title: "Messages slip through.",
    body: "You opened it on your phone, PadSplit marked it read, and three days later a member is angry.",
  },
  {
    title: "You can't verify vendors.",
    body: "Photos that might be from last month, and invoices you can't match to a job.",
  },
  {
    title: "Your numbers are a month late.",
    body: "Statements downloaded, emailed and reconciled weeks after the fact.",
  },
];

/**
 * Homepage v2 (build F4, Appendix A1). Served at `/` only when the
 * HOMEPAGE_VERSION switch is v2; always reachable (noindexed) at `/v2`.
 * The page's SEO is applied by the route wrapper (`pages/home.tsx`).
 */
export default function HomeV2() {
  return (
    <div className="flex min-h-[100dvh] flex-col bg-[#fffdf8]" data-testid="page-home-v2">
      <a
        href="#main-content"
        className="sr-only z-50 rounded-md bg-white px-4 py-3 text-brand shadow focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        Skip to main content
      </a>
      <PublicHeader />
      <main id="main-content" className="flex-grow outline-none" tabIndex={-1}>
        <HomeV2Hero />
        {/* 2. Proof */}
        <HomeV2Proof />

        {/* 3. Sound familiar? */}
        <section className="bg-[#edf0ed] px-4 py-14 md:py-20" data-testid="section-pains">
          <div className="mx-auto max-w-5xl">
            <div className="max-w-xl">
              <p className="text-xs font-bold uppercase tracking-[.15em] text-brand">The everyday friction</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-[#183347] md:text-4xl">
                The details add up fast.
              </h2>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {PAINS.map((p) => (
                <div key={p.title} className="min-w-0 rounded-xl border border-[#d9ded9] bg-[#fffdf8] p-5 sm:p-6">
                  <span className="font-mono text-xs font-bold tracking-widest text-[#87938d]">HOST NOTE</span>
                  <h3 className="mt-3 text-lg font-bold text-[#183347]">{p.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-[#52636a]">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. Product proof */}
        <HomeV2ProductEvidence />

        {/* 5. Five outcomes */}
        <section className="bg-[#fffdf8] px-4 py-16 md:py-20" data-testid="section-outcomes">
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="max-w-xl">
                <p className="text-xs font-bold uppercase tracking-[.15em] text-brand">One workspace, house by house</p>
                <h2 className="mt-3 text-3xl font-bold tracking-tight text-[#183347] md:text-4xl">
                  Keep the moving parts in view.
                </h2>
              </div>
              <p className="max-w-sm text-sm leading-6 text-[#52636a]">
                PadPulse gives you a clear place to coordinate. You decide how your team handles each task.
              </p>
            </div>
            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {OUTCOME_CARDS.map((card, i) => {
                const Icon = OUTCOME_ICONS[i] ?? Users;
                return (
                  <Link
                    key={card.title}
                    href={card.href}
                    className="group flex min-w-0 flex-col rounded-xl border border-[#ddded7] bg-[#f8f6f0] p-5 transition-colors hover:border-brand/40 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand sm:p-6"
                    data-testid={`card-outcome-${i}`}
                  >
                    <Icon size={20} className="text-brand" aria-hidden="true" />
                    <h3 className="mt-3 text-base font-bold text-[#183347] group-hover:text-brand">{card.title}</h3>
                    <p className="mt-2 flex-grow text-sm leading-6 text-[#52636a]">{card.body}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand">
                      Learn more <ArrowRight size={14} aria-hidden="true" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* 6. Clear onboarding */}
        <HomeV2Setup />
        <HomeV2Founder />

        {/* 7. Rooms off PadSplit too? */}
        <section className="overflow-hidden bg-brand-dark px-4 py-14 md:py-20" data-testid="section-off-padsplit">
          <div className="mx-auto grid max-w-5xl items-center gap-10 md:grid-cols-[1fr_auto]">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.15em] text-brand-accent">On PadSplit and beyond</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-[#fff9ef] md:text-4xl">
                Some rooms on PadSplit. Some off?
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-7 text-[#dbe5e4]">
                Give off-PadSplit rooms your own direct-booking site, listing tracker and booking form, alongside the
                same tools for locks, maintenance and property finances.
              </p>
              <p className="mt-4 max-w-2xl text-sm leading-6 text-[#c5d3d0]">
                For rooms off PadSplit,{" "}
                <strong className="text-white">your own member portal handles messages and booking requests.</strong>
              </p>
            </div>
            <div
              className="rounded-xl border border-white/15 bg-white/[.07] px-7 py-6 text-center md:min-w-[16rem]"
              data-testid="stat-saved-per-booking"
            >
              <p className="text-3xl font-bold text-brand-accent">$250–$300</p>
              <p className="mt-2 text-sm leading-5 text-[#dbe5e4]">Saved per booking vs. PadSplit fees</p>
            </div>
          </div>
        </section>

        {/* 8. Comparison */}
        <section className="bg-[#f6f3eb] px-4 py-14 md:py-20" data-testid="section-comparison">
          <div className="mx-auto max-w-5xl">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[.15em] text-brand">Keep using PadSplit</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-[#183347] md:text-4xl">
                PadSplit, with a clearer place to run the rest.
              </h2>
            </div>
            <div className="mt-8 hidden overflow-x-auto rounded-xl border border-[#deded5] bg-[#fffdf8] md:block">
            <table className="w-full text-left" data-testid="table-comparison">
              <thead>
                <tr className="border-b border-gray-200 text-sm text-gray-500">
                  <th scope="col" className="py-3 pr-4 font-medium">Job</th>
                  <th scope="col" className="py-3 pr-4 font-medium">PadSplit dashboard</th>
                  <th scope="col" className="py-3 font-medium">With PadPulse</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.job} className="border-b border-[#efeee8] align-top last:border-b-0">
                    <th scope="row" className="px-5 py-4 pr-4 font-semibold text-[#183347]">{row.job}</th>
                    <td className="py-4 pr-4 text-[#617074]">{row.padsplit}</td>
                    <td className="py-4 pr-5 font-medium text-[#344c50]">{row.padpulse}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="mt-6 space-y-3 md:hidden" data-testid="list-comparison-mobile">
              {COMPARISON_ROWS.map((row) => (
                <div key={row.job} className="min-w-0 rounded-xl border border-[#deded5] bg-[#fffdf8] p-4">
                  <p className="font-semibold text-[#183347]">{row.job}</p>
                  <p className="mt-2 text-sm leading-5 text-[#617074]">PadSplit dashboard: {row.padsplit}</p>
                  <p className="mt-1 text-sm leading-5 font-medium text-[#344c50]">With PadPulse: {row.padpulse}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 9. Pricing, FAQ */}
        <section className="overflow-hidden bg-[#edf0ed] px-4 py-14 md:py-20" data-testid="section-home-pricing">
          <div className="mx-auto max-w-6xl px-0.5 sm:px-1">
          <p className="text-center text-xs font-bold uppercase tracking-[.15em] text-brand">Straightforward by property</p>
          <h2 className="mt-3 text-center text-3xl font-bold tracking-tight text-[#183347] md:text-4xl">PadPulse pricing</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-6 text-[#52636a]">
            Choose Research, Host, or Property Manager. AI-drafted replies are available only with beta access;
            continuous Auto Draft also requires beta access and enabling the feature. A signup or paid plan alone
            does not grant access.
          </p>
          <div className="mt-9">
            <PricingCards location="home_v2_pricing" />
          </div>
          <p className="mt-8 text-center">
            <Link href="/pricing" className="font-semibold text-brand hover:underline" data-testid="link-see-full-pricing">
              See full pricing
            </Link>
          </p>
          </div>
        </section>

        <section className="bg-[#fffdf8] px-4 py-14 md:py-20">
          <div className="mx-auto max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[.15em] text-brand">Good to know</p>
            <h2 className="mb-8 mt-3 text-3xl font-bold tracking-tight text-[#183347]">Questions, answered.</h2>
            <FaqList items={HOME_FAQ} testId="section-home-faq" />
          </div>
        </section>
        <FinalBand location="home_v2" />
      </main>
      <PublicFooter />
    </div>
  );
}
